package lacaravana.ui

import kotlinx.browser.document
import kotlinx.coroutines.launch
import kotlinx.html.*
import kotlinx.html.dom.append
import kotlinx.html.js.onClickFunction
import lacaravana.app.Database
import lacaravana.app.appScope
import lacaravana.domain.Caja
import lacaravana.domain.CajaData
import lacaravana.domain.Menu
import lacaravana.domain.Pedido
import org.w3c.dom.Element

object CajaView {
    private fun renderClosed(content: Element, caja: CajaData, db: Database) {
        content.append {
            h2 { +"Caja" }
            div("card") {
                id = "caja-status"
                attributes["data-estado"] = "cerrada"
                h3 { +"Caja Cerrada" }
                p { +"Turno: ${caja.turno}" }
                p { +"Total: ${Menu.formatPrice(caja.totalCents)}" }
            }
            button {
                id = "nueva-caja"
                +"Abrir nueva caja"
                onClickFunction = {
                    appScope.launch {
                        Caja.open(db)
                        render(db)
                    }
                }
            }
        }
    }

    suspend fun render(db: Database) {
        val content = document.getElementById("content") ?: return
        content.innerHTML = ""

        var caja = Caja.findOpen(db)
        if (caja == null) {
            val lastClosed = db.query(
                "SELECT id, fecha, turno, estado, total_cents FROM caja ORDER BY updated_at DESC LIMIT 1"
            ).firstOrNull()?.let {
                CajaData(
                    id = it[0] as String, fecha = it[1] as String,
                    turno = (it[2] as Number).toInt(), estado = it[3] as String,
                    totalCents = (it[4] as Number).toInt()
                )
            }
            if (lastClosed != null && lastClosed.estado == "cerrada") {
                renderClosed(content, lastClosed, db)
                return
            }
            Caja.open(db)
            caja = Caja.findOpen(db) ?: return
        }
        val cajaId = caja.id
        val tickets = Caja.ticketsFor(db, cajaId)

        val openPedidos = db.query("SELECT id, mesa_id FROM pedidos WHERE estado = 'abierto'")

        data class PedidoSummary(val id: String, val mesaId: Int, val totalCents: Int)

        val pedidoSummaries = openPedidos.map { row ->
            val pid = row[0] as String
            val mid = (row[1] as Number).toInt()
            val items = Pedido.itemsFor(db, pid)
            PedidoSummary(pid, mid, items.sumOf { it.precioCents * it.cantidad })
        }

        content.append {
            h2 { +"Caja" }
            div("card") {
                id = "caja-status"
                attributes["data-estado"] = caja.estado
                h3 { +"Caja ${if (caja.estado == "abierta") "Abierta" else "Cerrada"}" }
                p { +"Turno: ${caja.turno}" }
            }

            if (pedidoSummaries.isNotEmpty()) {
                h3 { +"Pedidos abiertos" }
                table {
                    thead {
                        tr {
                            th { +"Mesa" }
                            th { +"Total" }
                            th { }
                        }
                    }
                    tbody {
                        for (ps in pedidoSummaries) {
                            tr {
                                attributes["data-pedido"] = ps.id
                                td { +"Mesa ${ps.mesaId}" }
                                td { +Menu.formatPrice(ps.totalCents) }
                                td {
                                    button {
                                        attributes["data-action"] = "cobrar"
                                        +"Cobrar efectivo"
                                        onClickFunction = {
                                            appScope.launch {
                                                Caja.cobrar(db, cajaId, ps.id, "efectivo")
                                                render(db)
                                            }
                                        }
                                    }
                                    button {
                                        attributes["data-action"] = "cobrar-tarjeta"
                                        +"Cobrar tarjeta"
                                        onClickFunction = {
                                            appScope.launch {
                                                Caja.cobrar(db, cajaId, ps.id, "tarjeta")
                                                render(db)
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }

            if (tickets.isNotEmpty()) {
                h3 { +"Tickets" }
                table {
                    thead {
                        tr {
                            th { +"Ticket" }
                            th { +"Total" }
                            th { +"Pago" }
                        }
                    }
                    tbody {
                        for (t in tickets) {
                            tr {
                                attributes["data-ticket"] = t.id
                                td { +t.id.takeLast(8) }
                                td { +Menu.formatPrice(t.totalCents) }
                                td { +t.metodoPago }
                            }
                        }
                    }
                }
                val totalRecaudado = tickets.sumOf { it.totalCents }
                p { id = "caja-total"; +"Total recaudado: ${Menu.formatPrice(totalRecaudado)}" }
            }

            if (caja.estado == "abierta") {
                button(classes = "danger") {
                    id = "cerrar-caja"
                    +"Cerrar Caja"
                    onClickFunction = {
                        appScope.launch {
                            Caja.close(db, cajaId)
                            render(db)
                        }
                    }
                }
            }
        }
    }
}
